import React, { useMemo, useState } from 'react';

import classNames from 'classnames';
import { Button, Col, Row, Spinner } from 'react-bootstrap';
import { css, StyleSheet } from 'aphrodite';
import { faFilter, faFileExport } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Swal from 'sweetalert2';
import SwalDialog from 'lib/utils/toast';
import { useDebounce } from 'use-debounce';
import { useHistory } from 'react-router-dom';

import {
  ALL_PRODUCT_LISTS,
  GLOBAL_MANUFACTURERS,
  GLOBAL_MANUFACTURERS_DELETE_UNLINKED,
  GLOBAL_MANUFACTURERS_DOWNLOAD_CSV,
  GLOBAL_MANUFACTURERS_UNVERIFY,
} from 'lib/networking/endpoints';
import { areYouSure } from 'lib/utils/toast';
import {
  backgroundGrey5,
  blackberry,
  bootstrapDanger,
  coolGrey,
  grape,
  greyStroke,
  purple1,
  squidInk,
  white,
} from 'lib/css/colors';
import ActiveFilterChips from 'components/shared/ActiveFilterChips';
import Container from 'components/shared/Container';
import { DEFAULT_PAGE_SIZE } from 'lib/constants';
import LoadingSpinner from 'components/shared/LoadingSpinner';
import ManageManufacturersTable from 'components/manage_manufacturers/ManageManufacturersTable';
import ManufacturerEditModal from 'components/manage_manufacturers/ManufacturerEditModal';
import ManufacturerFilters from 'components/manage_manufacturers/filters/ManufacturerFilters';
import { ManufacturerVerificationStatusOptions } from 'lib/enums/ManufacturerVerificationStatus';
import MergeManufacturersModal from 'components/manage_manufacturers/MergeManufacturersModal';
import PaginationComponent from 'components/shared/PaginationComponent';
import ProductListFilter from 'lib/enums/ProductListFilter';
import SearchBar from 'components/shared/SearchBar';
import useGet from 'lib/hooks/useGet';
import useQueryString from 'lib/hooks/useQueryString';
import usePost from 'lib/hooks/usePost';

function ManageManufacturersView() {
  const history = useHistory();
  const verificationStatus = useQueryString().get('verificationStatus');

  const [searchInput, setSearchInput] = useState('');
  const [searchQuery] = useDebounce(searchInput, 200);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(DEFAULT_PAGE_SIZE);
  const [mergeOrDeleteList, setMergeOrDeleteList] = useState([]);
  const [showMergeModal, setShowMergeModal] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [productList, setProductList] = useState();
  const [status, setStatus] = useState(verificationStatus || '');
  const [manufacturerEditModalSettings, setManufacturerEditModalSettings] = useState({
    show: false,
  });

  const {
    data: { manufacturers, count } = {},
    loading,
    refetch,
  } = useGet(GLOBAL_MANUFACTURERS, {
    limit: itemsPerPage,
    offset: itemsPerPage * (currentPage - 1),
    query: searchQuery,
    gln_search: searchQuery,
    product_list_id: productList?.id,
    status: status,
  });

  const { postData: deleteManufacturers, loading: manufacturersDeleting } = usePost(
    GLOBAL_MANUFACTURERS_DELETE_UNLINKED,
    () => {
      SwalDialog('success', 'Successfully deleted manufacturers', 'Success', 'center');
      refetch();
      setMergeOrDeleteList([]);
    },
    error => {
      SwalDialog(
        'error',
        `An error occurred while deleting manufacturers: ${error?.response?.data?.message}`,
        'Error',
        'center',
      );
    },
  );

  const { postData: unverifyManufacturers, loading: manufacturersUnverifying } = usePost(
    GLOBAL_MANUFACTURERS_UNVERIFY,
    () => {
      SwalDialog('success', 'Successfully unverified manufacturers', 'Success', 'center');
      refetch();
      setMergeOrDeleteList([]);
    },
    error => {
      SwalDialog(
        'error',
        `An error occurred while unverifying manufacturers: ${error?.response?.data?.message}`,
        'Error',
        'center',
      );
    },
  );

  const { postData: exportManufacturerDetails, loading: manufacturersExporting } = usePost(
    GLOBAL_MANUFACTURERS_DOWNLOAD_CSV,
    () =>
      Swal.fire({
        icon: 'success',
        html: `
        <div>
          The export csv file will be generated in a while.
          You can download the generated file from\n
          <a
            href="/file-exports"
            rel="noopener noreferrer"
            target="_blank"
          >
            File Exports Page
          </a>
        </div>`,
        title: 'Success',
        position: 'center',
      }),
    () =>
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'An error occurred while trying to export the file',
      }),
  );

  const { data: { product_lists = [] } = {} } = useGet(ALL_PRODUCT_LISTS);

  const productListOptions = useMemo(() => {
    return product_lists
      .filter(list => list?.filters && !(ProductListFilter.MANUFACTURER_IDS in list.filters))
      .map(list => ({ label: list.name, value: list.id }));
  }, [product_lists]);

  const activeFilters = [
    productList && {
      label: 'Product List',
      value: productList.name,
      onRemove: () => setProductList(undefined),
    },
    status && {
      label: 'Status',
      value:
        ManufacturerVerificationStatusOptions.find(option => option.value === status)?.label ||
        status,
      onRemove: () => setStatus(''),
    },
  ].filter(Boolean);

  const clearAllFilters = () => {
    setProductList(undefined);
    setStatus('');
  };

  return (
    <div className={css(styles.pageContainer)}>
      <div className={css(styles.headerSection)}>
        <div className={css(styles.headerContent)}>
          <h1 className={css(styles.pageTitle)}>Manage Manufacturers</h1>
        </div>
      </div>

      <Container className={css(styles.mainContent)}>
        <Row className={css(styles.toolbarRow)}>
          <Col xs={5} className="d-flex align-items-center">
            <div className={css(styles.searchWrapper)}>
              <SearchBar
                value={searchInput}
                setValue={setSearchInput}
                placeholder="Search by manufacturer name or GLN"
              />
            </div>
            <Button
              className={css(styles.filterButton)}
              onClick={() => setShowFilters(!showFilters)}
            >
              <FontAwesomeIcon icon={faFilter} className="mr-2" />
              {showFilters ? 'Hide' : 'Show'} Filters
            </Button>
          </Col>
          <Col xs={7} className={css(styles.actionButtonsCol)}>
            <Button
              className={css(styles.dangerButton)}
              onClick={() =>
                areYouSure(
                  () =>
                    deleteManufacturers({
                      deleteIds: mergeOrDeleteList.map(manufacturer => manufacturer.id),
                    }),
                  'Are you sure you want to delete the selected manufacturers?',
                )
              }
              disabled={mergeOrDeleteList.length < 1 || manufacturersDeleting}
            >
              {manufacturersDeleting ? (
                <Spinner animation="border" role="status" size="sm" />
              ) : (
                'Delete Unlinked Manufacturers'
              )}
            </Button>
            <Button
              className={css(styles.outlineDangerButton)}
              onClick={() =>
                areYouSure(
                  () =>
                    unverifyManufacturers({
                      manufacturer_ids: mergeOrDeleteList.map(manufacturer => manufacturer.id),
                    }),
                  'Are you sure you want to unverify the selected manufacturers?',
                )
              }
              disabled={mergeOrDeleteList.length < 1 || manufacturersUnverifying}
            >
              {manufacturersUnverifying ? (
                <Spinner animation="border" role="status" size="sm" />
              ) : (
                'Unverify Selected Manufacturers'
              )}
            </Button>
            <Button
              className={css(styles.secondaryButton)}
              onClick={() => setShowMergeModal(true)}
              disabled={mergeOrDeleteList.length < 2}
            >
              Merge Manufacturers
            </Button>
            <Button
              className={css(styles.primaryButton)}
              onClick={() => history.push('/enrich-products/create-manufacturer')}
            >
              + New Manufacturer
            </Button>
            <Button
              className={css(styles.outlineButton)}
              onClick={() =>
                exportManufacturerDetails({
                  query: searchQuery,
                  product_list_id: productList?.id,
                  status: status,
                })
              }
              disabled={manufacturersExporting}
            >
              <FontAwesomeIcon icon={faFileExport} className="mr-2" />
              Export
            </Button>
          </Col>
        </Row>
        <Row>
          <Col className="d-flex align-items-center flex-wrap mt-3">
            <span className={classNames(css(styles.selectedText), 'text-secondary ml-2 mr-3')}>
              {count} Manufacturers
            </span>
            <ActiveFilterChips filters={activeFilters} onClearAll={clearAllFilters} />
          </Col>
          <Col className={classNames(css(styles.pagination), 'd-flex justify-content-end')}>
            <PaginationComponent
              totalItems={count}
              itemsPerPage={itemsPerPage}
              setItemsPerPage={setItemsPerPage}
              currentPage={currentPage}
              setCurrentPage={setCurrentPage}
              defaultSize
            />
          </Col>
        </Row>
        <div className={css(styles.scrollableContainer)}>
          {loading ? (
            <LoadingSpinner short />
          ) : (
            <ManageManufacturersTable
              manufacturers={manufacturers}
              mergeList={mergeOrDeleteList}
              setMergeList={setMergeOrDeleteList}
              setManufacturerEditModalSettings={setManufacturerEditModalSettings}
            />
          )}
        </div>
        {showFilters && (
          <div className={css(styles.manufacturerFiltersOverlay)}>
            <ManufacturerFilters
              productList={productList}
              setProductList={setProductList}
              status={status}
              setStatus={setStatus}
              setShowFilters={setShowFilters}
              loading={loading}
              product_lists={product_lists}
              productListOptions={productListOptions}
            />
          </div>
        )}
        {showMergeModal ? (
          <MergeManufacturersModal
            mergeList={mergeOrDeleteList}
            setMergeList={setMergeOrDeleteList}
            onHide={() => {
              setShowMergeModal(false);
              refetch();
              setMergeOrDeleteList([]);
            }}
          />
        ) : null}
        {manufacturerEditModalSettings.show ? (
          <ManufacturerEditModal
            show={manufacturerEditModalSettings.show}
            manufacturer={manufacturerEditModalSettings.manufacturer}
            onHide={() => {
              setManufacturerEditModalSettings({ show: false });
              setMergeOrDeleteList([]);
            }}
            refetch={refetch}
          />
        ) : null}
      </Container>
    </div>
  );
}

const styles = StyleSheet.create({
  pageContainer: {
    minHeight: '100vh',
    backgroundColor: backgroundGrey5,
  },
  headerSection: {
    background: `linear-gradient(135deg, ${blackberry} 0%, ${purple1} 100%)`,
    padding: '24px 40px',
  },
  headerContent: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    maxWidth: 1600,
    margin: '0 auto',
  },
  pageTitle: {
    fontSize: 28,
    fontWeight: 600,
    color: white,
    margin: 0,
  },
  mainContent: {
    padding: '24px 40px',
  },
  toolbarRow: {
    marginBottom: 16,
    alignItems: 'center',
  },
  searchWrapper: {
    flex: 1,
    maxWidth: 300,
  },
  filterButton: {
    backgroundColor: 'transparent',
    color: grape,
    border: `1px solid ${greyStroke}`,
    borderRadius: 8,
    padding: '8px 16px',
    fontWeight: 500,
    marginLeft: 12,
    whiteSpace: 'nowrap',
    ':hover': {
      backgroundColor: `${grape}15`,
      borderColor: grape,
    },
  },
  actionButtonsCol: {
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 8,
  },
  primaryButton: {
    backgroundColor: blackberry,
    color: white,
    border: 'none',
    borderRadius: 8,
    padding: '8px 16px',
    fontWeight: 600,
    fontSize: 14,
    whiteSpace: 'nowrap',
    ':hover': {
      backgroundColor: grape,
    },
  },
  secondaryButton: {
    backgroundColor: greyStroke,
    color: squidInk,
    border: 'none',
    borderRadius: 8,
    padding: '8px 16px',
    fontWeight: 600,
    fontSize: 14,
    whiteSpace: 'nowrap',
    ':hover': {
      backgroundColor: coolGrey,
      color: white,
    },
  },
  outlineButton: {
    backgroundColor: 'transparent',
    color: grape,
    border: `2px solid ${grape}`,
    borderRadius: 8,
    padding: '8px 16px',
    fontWeight: 600,
    fontSize: 14,
    whiteSpace: 'nowrap',
    ':hover': {
      backgroundColor: grape,
      color: white,
    },
  },
  dangerButton: {
    backgroundColor: bootstrapDanger,
    color: white,
    border: 'none',
    borderRadius: 8,
    padding: '8px 16px',
    fontWeight: 600,
    fontSize: 14,
    whiteSpace: 'nowrap',
    ':hover': {
      opacity: 0.85,
    },
  },
  outlineDangerButton: {
    backgroundColor: 'transparent',
    color: bootstrapDanger,
    border: `2px solid ${bootstrapDanger}`,
    borderRadius: 8,
    padding: '8px 16px',
    fontWeight: 600,
    fontSize: 14,
    whiteSpace: 'nowrap',
    ':hover': {
      backgroundColor: bootstrapDanger,
      color: white,
    },
  },
  scrollableContainer: {
    height: '70vh',
    backgroundColor: white,
    borderRadius: 12,
    border: `1px solid ${greyStroke}`,
    overflow: 'auto',
  },
  manufacturerFiltersOverlay: {
    position: 'absolute',
    padding: '1.5rem',
    top: 40,
    right: 0,
    bottom: 0,
    width: '30vw',
    minHeight: '100vh',
    backgroundColor: white,
    borderLeft: `4px solid ${grape}`,
    transitionProperty: 'transform, visibility, width',
    transitionDuration: '0.4s',
    transitionTimingFunction: 'ease-in-out',
    zIndex: 999,
    visibility: 'visible',
  },
  pagination: {
    marginTop: '1rem',
    zIndex: 2,
  },
  selectedText: {
    fontSize: '1rem',
    color: coolGrey,
  },
});

export default ManageManufacturersView;
